import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ApiError, api } from "../api/client";
import type {
  GuestHomeStyleProjectDto,
  GuestHomeStyleSession,
  GuestIntakeSubmissionInput,
  GuestInviteConsumption,
} from "../api/types";
import { errorMessage } from "../utils/errors";
import "../project-selmo-design-system.css";
import "./guest-home-style-portal.css";

type GuestScreen = "login" | "onboarding" | "projects" | "viewer" | "intake";
type GuestLoginMethod = "password" | "google" | "both";

export interface GuestAddressSelection {
  roadAddress: string;
  lotAddress: string;
  postalCode: string;
  addressNeighborhood?: string;
  latitude: number;
  longitude: number;
}

export interface GuestHomeStylePortalProps {
  portalHostname?: string;
  inviteToken?: string;
  initialProjectId?: string;
  initialView?: "projects" | "project" | "intake";
  requestGoogleCredential?: () => Promise<string>;
  selectKakaoAddress?: () => Promise<GuestAddressSelection | undefined>;
  mountKakaoMap?: (container: HTMLElement, selection: GuestAddressSelection) => void | (() => void) | Promise<void | (() => void)>;
  navigate?: (path: string) => void;
  cleanInviteUrl?: () => void;
}

function statusLabel(value: GuestHomeStyleProjectDto["stages"][number]["status"]) {
  return ({ waiting: "예정", in_progress: "진행 중", completed: "완료", skipped: "해당 없음" } as const)[value];
}

function projectStatusLabel(project: GuestHomeStyleProjectDto) {
  if (project.primaryStatus === "operating") return "운영";
  if (project.workflowStatus === "completed" || project.primaryStatus === "completed") return "완료";
  if (project.workflowStatus === "ready_to_complete") return "완료 확인 중";
  return "진행중";
}

function projectStatusTone(project: GuestHomeStyleProjectDto) {
  if (project.primaryStatus === "operating") return "is-operating";
  if (project.workflowStatus === "completed" || project.primaryStatus === "completed") return "is-complete";
  return "";
}

function currentStageLabel(project: GuestHomeStyleProjectDto) {
  return project.stages.find((stage) => stage.code === project.currentStage)?.label ?? "단계 확인 중";
}

function progressPercent(project: GuestHomeStyleProjectDto) {
  return Math.min(100, Math.max(0, Math.round((project.progress.completed / Math.max(1, project.progress.total)) * 100)));
}

function GuestHeader({ session, onLogout, portalHostname }: {
  session?: GuestHomeStyleSession;
  onLogout: () => void;
  portalHostname: string;
}) {
  return <header className="guest-hs-header">
    <a className="guest-hs-brand" href="/" aria-label="Project Selmo 고객 홈">
      <span aria-hidden="true">ㅅ</span>
      <strong>PROJECT SELMO<small>고객 프로젝트</small></strong>
    </a>
    <span className="guest-hs-domain"><i aria-hidden="true" />안전한 고객 전용 공간 · {portalHostname}</span>
    <div className="guest-hs-header-actions">
      {session?.account?.displayName ? <span className="guest-hs-account"><b aria-hidden="true">{session.account.displayName.slice(0, 1)}</b><small>{session.account.displayName}</small></span> : null}
      {session?.authenticated ? <button type="button" onClick={onLogout}>로그아웃</button> : null}
    </div>
  </header>;
}

function LoginScreen({ onPassword, onGoogle, googleAvailable, message, busy }: {
  onPassword: (identifier: string, password: string) => void;
  onGoogle: () => void;
  googleAvailable: boolean;
  message?: string;
  busy: boolean;
}) {
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    onPassword(String(form.get("email") ?? "").trim(), String(form.get("password") ?? ""));
  };
  return <main className="guest-hs-login">
    <section className="guest-hs-login-intro">
      <p className="guest-hs-eyebrow ps-section-kicker">PROJECT LINK</p>
      <h1>내 공간의 변화를<br />한눈에 확인하세요.</h1>
      <p>초대받은 계정으로 로그인하면 연결된 홈스타일 프로젝트의 현재 단계와 공개 자료를 안전하게 볼 수 있습니다.</p>
      <div className="guest-hs-login-project ps-card">
        <span className="ps-status-chip">고객 전용</span>
        <strong>프로젝트 상태 · 일정 · 공개 자료</strong>
        <ul><li>현재 진행 단계와 전체 일정</li><li>공개 승인된 디자인·사진·구매 자료</li><li>고객 정보 제출과 운영자 검토</li></ul>
      </div>
    </section>
    <form className="guest-hs-auth-card ps-card" onSubmit={submit}>
      <div><p className="guest-hs-eyebrow ps-section-kicker">GUEST SIGN IN</p><h2>고객 로그인</h2><p>초대받은 이메일 계정으로 로그인해 주세요.</p></div>
      <label><span>이메일</span><input name="email" type="email" autoComplete="username" placeholder="name@example.com" required /></label>
      <label><span>비밀번호</span><input name="password" type="password" autoComplete="current-password" required /></label>
      <button className="is-primary" type="submit" disabled={busy}>{busy ? "로그인 중…" : "이메일로 로그인"}</button>
      <div className="guest-hs-divider"><span>또는</span></div>
      <button type="button" disabled={busy || !googleAvailable} onClick={onGoogle}>Google로 계속하기</button>
      {!googleAvailable ? <small>Google 인증 설정을 확인하고 있습니다.</small> : null}
      {message ? <p className="guest-hs-message is-error" role="alert">{message}</p> : null}
    </form>
  </main>;
}

function OnboardingScreen({ invite, requestGoogleCredential, onDone }: {
  invite: GuestInviteConsumption;
  requestGoogleCredential?: () => Promise<string>;
  onDone: (session: GuestHomeStyleSession) => void;
}) {
  const [method, setMethod] = useState<GuestLoginMethod>("password");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setMessage(undefined);
    try {
      const googleCredential = method === "password" ? undefined : await requestGoogleCredential?.();
      if (method !== "password" && !googleCredential) throw new Error("Google 인증을 완료해 주세요.");
      const password = String(form.get("password") ?? "");
      onDone(await api.guestOnboarding(method === "password"
        ? { method, onboardingTicket: invite.onboardingTicket, password }
        : method === "google"
          ? { method, onboardingTicket: invite.onboardingTicket, credential: googleCredential! }
          : { method, onboardingTicket: invite.onboardingTicket, password, credential: googleCredential! }));
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  return <main className="guest-hs-onboarding">
    <form className="guest-hs-auth-card ps-card" onSubmit={submit}>
      <div><p className="guest-hs-eyebrow ps-section-kicker">FIRST SIGN IN</p><h1>고객 계정 만들기</h1><p><strong>{invite.projectName}</strong> 초대를 <b>{invite.maskedEmail}</b> 계정으로 연결합니다.</p></div>
      <fieldset><legend>로그인 방식</legend>{(["password", "google", "both"] as const).map((value) => { const unavailable = value !== "password" && !requestGoogleCredential; return <label key={value} aria-disabled={unavailable}><input type="radio" name="method" value={value} checked={method === value} disabled={unavailable} onChange={() => setMethod(value)} /><span><strong>{value === "password" ? "비밀번호" : value === "google" ? "Google" : "비밀번호 + Google"}</strong><small>{unavailable ? "Google 로그인 설정 후 사용 가능" : value === "both" ? "두 방법 중 편한 방식으로 로그인" : `${value === "google" ? "Google 계정" : "비밀번호"}으로 로그인`}</small></span></label>; })}</fieldset>
      {method !== "google" ? <label><span>비밀번호</span><input name="password" type="password" minLength={10} autoComplete="new-password" required /></label> : null}
      <p className="guest-hs-security-note">초대 링크는 이 계정에 한 번만 연결되며, 다른 프로젝트에는 접근할 수 없습니다.</p>
      <button className="is-primary" type="submit" disabled={busy}>{busy ? "계정 생성 중…" : "계정 만들고 프로젝트 열기"}</button>
      {message ? <p className="guest-hs-message is-error" role="alert">{message}</p> : null}
    </form>
  </main>;
}

function ProjectList({ projects, onOpen, onIntake }: {
  projects: GuestHomeStyleProjectDto[];
  onOpen: (id: string) => void;
  onIntake: (id: string) => void;
}) {
  return <main className="guest-hs-projects">
    <header className="guest-hs-page-heading">
      <div><p className="guest-hs-eyebrow ps-section-kicker">MY PROJECTS</p><h1>내 프로젝트</h1><p>초대로 연결된 프로젝트의 현재 상태와 공개 자료를 확인할 수 있습니다.</p></div>
      <span className="ps-status-chip">연결된 프로젝트 {projects.length}</span>
    </header>
    {projects.length ? <div className="guest-hs-project-grid">{projects.map((project) => {
      const percent = progressPercent(project);
      const titleId = `guest-project-${project.publicProjectId}`;
      return <article className="ps-card" key={project.publicProjectId} aria-labelledby={titleId}>
        <header>
          <span className={`ps-status-chip ${projectStatusTone(project)}`}>{projectStatusLabel(project)}</span>
          <div><strong id={titleId}>{project.projectName}</strong><small>{project.neighborhood ?? "주소 확인 중"} · 현재 {currentStageLabel(project)}</small></div>
        </header>
        <div className="guest-hs-project-progress" aria-label={`전체 ${project.progress.total}단계 중 ${project.progress.completed}단계 완료`}>
          <div><span>프로젝트 진행률</span><strong>{percent}%</strong></div>
          <span className="ps-progress-track"><i style={{ width: `${percent}%` }} /></span>
          <small>{project.progress.completed} / {project.progress.total} 단계 완료</small>
        </div>
        <footer><button type="button" onClick={() => onIntake(project.publicProjectId)}>정보 작성</button><button className="is-primary" type="button" onClick={() => onOpen(project.publicProjectId)}>프로젝트 보기 <span aria-hidden="true">›</span></button></footer>
      </article>;
    })}</div> : <section className="guest-hs-empty ps-card" role="status"><span aria-hidden="true">ㅅ</span><h2>연결된 프로젝트가 없습니다.</h2><p>초대 링크를 받은 계정으로 로그인했는지 확인해 주세요.</p></section>}
  </main>;
}

function ProjectViewer({ project, onBack, onIntake, mountKakaoMap }: {
  project: GuestHomeStyleProjectDto;
  onBack: () => void;
  onIntake: () => void;
  mountKakaoMap?: GuestHomeStylePortalProps["mountKakaoMap"];
}) {
  const [expanded, setExpanded] = useState<string>(project.currentStage ?? "");
  const percent = progressPercent(project);
  const currentStageIndex = project.stages.findIndex((stage) => stage.code === project.currentStage);
  const currentStageNumber = currentStageIndex >= 0
    ? currentStageIndex + 1
    : Math.min(Math.max(1, project.progress.total), project.progress.completed + 1);
  const coordinateVerified = project.coordinateVerified === true;
  const location = useMemo<GuestAddressSelection | undefined>(
    () => coordinateVerified && project.latitude !== null && project.longitude !== null
      ? {
          roadAddress: project.address ?? "프로젝트 위치",
          lotAddress: "",
          postalCode: "",
          latitude: project.latitude,
          longitude: project.longitude,
        }
      : undefined,
    [coordinateVerified, project.address, project.latitude, project.longitude],
  );
  return <main className="guest-hs-viewer">
    <nav className="guest-hs-breadcrumb" aria-label="프로젝트 탐색"><button className="guest-hs-back" type="button" onClick={onBack}>← 내 프로젝트</button><span aria-hidden="true">/</span><strong>{project.projectName}</strong></nav>
    <section className="guest-hs-project-hero ps-card">
      <div><p className="guest-hs-eyebrow ps-section-kicker">HOME STYLE PROJECT</p><h1>{project.projectName}</h1><span className={`ps-status-chip ${projectStatusTone(project)}`}>{projectStatusLabel(project)}</span></div>
      <div className="guest-hs-hero-location"><small>현장 위치</small><strong>{project.neighborhood ?? "현장 확인 중"}</strong><span>{project.address ?? "주소 확인 중"} {project.addressDetail}</span></div>
    </section>
    <section className="guest-hs-readonly-facts" aria-label="계약 및 위치 정보">
      <dl className="ps-card"><div><dt>현재 단계</dt><dd>{currentStageLabel(project)}</dd></div><div><dt>계약일</dt><dd>{project.contractDate?.replaceAll("-", ".") ?? "미정"}</dd></div></dl>
      {location ? <KakaoMapPreview selection={location} mountKakaoMap={mountKakaoMap} /> : null}
    </section>
    <div className="guest-hs-viewer-grid">
      <aside className="ps-card"><p className="guest-hs-eyebrow ps-section-kicker">CURRENT STEP</p><h2>현재 진행</h2><div className="guest-hs-current-stage"><span>{String(currentStageNumber).padStart(2, "0")}</span><strong>{currentStageLabel(project)}</strong></div><div className="guest-hs-view-progress" aria-label={`전체 ${project.progress.total}단계 중 ${project.progress.completed}단계 완료`}><div><span>전체 진행률</span><b>{percent}%</b></div><span className="ps-progress-track"><i style={{ width: `${percent}%` }} /></span><small>{project.progress.completed} / {project.progress.total} 단계 완료</small></div><button type="button" onClick={onIntake}>고객 정보 작성·수정</button><small>작성 내용은 운영자 검토 후 반영됩니다.</small></aside>
      <section className="guest-hs-stage-list ps-card" aria-labelledby="guest-stage-list-title"><header><div><p className="guest-hs-eyebrow ps-section-kicker">PROJECT SCHEDULE</p><h2 id="guest-stage-list-title">1–10단계 일정</h2></div><span className="guest-hs-readonly-chip">읽기 전용</span></header><ol>{project.stages.map((stage, index) => {
        const panelId = `guest-stage-${stage.code}`;
        return <li className={`is-${stage.status}`} key={stage.code}><button type="button" aria-expanded={expanded === stage.code} aria-controls={panelId} onClick={() => setExpanded(expanded === stage.code ? "" : stage.code)}><b>{String(index + 1).padStart(2, "0")}</b><span><strong>{stage.label}</strong><small>{stage.description}</small></span><em>{statusLabel(stage.status)}</em><i aria-hidden="true">⌄</i></button>{expanded === stage.code ? <dl id={panelId}><div><dt>예정</dt><dd>{stage.scheduledOn?.replaceAll("-", ".") ?? "미정"}</dd></div><div><dt>완료</dt><dd>{stage.completedOn?.replaceAll("-", ".") ?? "—"}</dd></div></dl> : null}</li>;
      })}</ol></section>
    </div>
    {project.publishedAssets.length ? <section className="guest-hs-assets ps-card"><header><div><p className="guest-hs-eyebrow ps-section-kicker">PUBLISHED MATERIALS</p><h2>공개 자료</h2></div><small>운영자가 공개한 자료만 표시됩니다.</small></header><div>{project.publishedAssets.map((asset) => <a key={asset.publicAssetId} href={asset.proxyUrl} target="_blank" rel="noreferrer"><span aria-hidden="true">↗</span><strong>{asset.label ?? "공개 자료"}</strong><small>{asset.kind === "design" ? "디자인" : asset.kind === "purchase" ? "구매리스트" : asset.kind === "before_photo" ? "Before" : asset.kind === "after_photo" ? "After" : "시공"}</small></a>)}</div></section> : null}
  </main>;
}

function KakaoMapPreview({ selection, mountKakaoMap }: {
  selection: GuestAddressSelection;
  mountKakaoMap?: GuestHomeStylePortalProps["mountKakaoMap"];
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);
  useEffect(() => {
    const container = mapRef.current;
    if (!container || !mountKakaoMap) {
      setMapReady(false);
      return;
    }
    let disposed = false;
    let cleanup: void | (() => void);
    void Promise.resolve().then(() => mountKakaoMap(container, selection)).then((result) => {
      if (disposed) { if (typeof result === "function") result(); return; }
      cleanup = result;
      setMapReady(true);
    }).catch(() => {
      if (!disposed) { setMapReady(false); container.replaceChildren(); }
    });
    return () => { disposed = true; if (typeof cleanup === "function") cleanup(); };
  }, [mountKakaoMap, selection]);
  const addressLabel = selection.roadAddress || selection.lotAddress;
  return <div className={`guest-hs-map-preview ps-card${mapReady ? " is-ready" : " is-fallback"}`}>
    <div ref={mapRef} className="guest-hs-map-canvas" role="img" aria-label={`카카오 지도: ${addressLabel} 고정 위치, 마커 이동 불가`} />
    <p><strong>{mapReady ? "카카오 지도 위치 확인" : "지도를 표시하지 못했습니다"}</strong><span>{addressLabel}</span><small>{selection.latitude.toFixed(5)}, {selection.longitude.toFixed(5)} · 고정 마커</small></p>
  </div>;
}

function IntakeScreen({ project, onBack, selectKakaoAddress, mountKakaoMap }: { project: GuestHomeStyleProjectDto; onBack: () => void; selectKakaoAddress?: () => Promise<GuestAddressSelection | undefined>; mountKakaoMap?: GuestHomeStylePortalProps["mountKakaoMap"] }) {
  const [address, setAddress] = useState<GuestAddressSelection | undefined>();
  const [addressMode, setAddressMode] = useState<"search" | "manual">("manual");
  const [message, setMessage] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const input: GuestIntakeSubmissionInput = {
      name: String(form.get("name") ?? "").trim(),
      phone: String(form.get("phone") ?? "").trim(),
      roadAddress: String(form.get("roadAddress") ?? "").trim(),
      lotAddress: String(form.get("lotAddress") ?? "").trim() || null,
      postalCode: String(form.get("postalCode") ?? "").trim(),
      latitude: address?.latitude ?? null,
      longitude: address?.longitude ?? null,
      addressDetail: String(form.get("addressDetail") ?? "").trim() || null,
      accessPassword: String(form.get("accessPassword") ?? "") || null,
      occupancyStatus: String(form.get("occupancyStatus") ?? "unknown") as GuestIntakeSubmissionInput["occupancyStatus"],
      moveInNote: String(form.get("moveInNote") ?? "").trim() || null,
      specialNotes: String(form.get("specialNotes") ?? "").trim() || null,
      coordinateVerified: Boolean(address),
    };
    setBusy(true);
    setMessage(undefined);
    try {
      const result = await api.submitGuestIntake(project.publicProjectId, input);
      setMessage(result.status === "pending_review" ? "제출했습니다. 운영자 검토 후 프로젝트 정보에 반영됩니다." : "제출했습니다.");
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };
  const searchAddress = async () => {
    if (!selectKakaoAddress) return setMessage("카카오 주소 검색 설정을 확인하고 있습니다. 수동 입력도 가능합니다.");
    setBusy(true);
    try {
      const next = await selectKakaoAddress();
      if (next) { setAddress(next); setAddressMode("search"); setMessage("주소와 지도 위치를 확인했습니다."); }
    } catch (error) { setMessage(errorMessage(error)); } finally { setBusy(false); }
  };
  return <main className="guest-hs-intake">
    <nav className="guest-hs-breadcrumb" aria-label="프로젝트 탐색"><button className="guest-hs-back" type="button" onClick={onBack}>← 프로젝트로 돌아가기</button><span aria-hidden="true">/</span><strong>정보 작성</strong></nav>
    <header className="guest-hs-page-heading"><div><p className="guest-hs-eyebrow ps-section-kicker">CUSTOMER INFORMATION</p><h1>프로젝트 정보 작성</h1><p>현장 방문에 필요한 내용을 제출해 주세요. 원본은 운영자 검토 전까지 변경되지 않습니다.</p></div><span className="ps-status-chip is-waiting">검토 후 반영</span></header>
    <form onSubmit={submit}>
      <section className="ps-card">
        <header><span>01</span><div><h2>기본 정보</h2><p>연락 가능한 고객 정보를 입력해 주세요.</p></div></header>
        <label><span>이름</span><input name="name" autoComplete="name" required /></label>
        <label><span>연락처</span><input name="phone" type="tel" inputMode="tel" autoComplete="tel" required /></label>
      </section>
      <section className="ps-card">
        <header><span>02</span><div><h2>현장 주소</h2><p>검색으로 확인된 좌표는 고정 마커로 저장됩니다.</p></div></header>
        <div className="guest-hs-address-mode" role="group" aria-label="주소 입력 방식"><button type="button" aria-pressed={addressMode === "search"} onClick={() => void searchAddress()}>카카오 주소 검색</button><button type="button" aria-pressed={addressMode === "manual"} onClick={() => { setAddress(undefined); setAddressMode("manual"); }}>수동 입력</button></div>
        <label className="is-wide"><span>도로명 주소</span><input key={`road-${address?.roadAddress}`} name="roadAddress" defaultValue={address?.roadAddress ?? project.address ?? ""} required /></label>
        <label><span>지번 주소</span><input key={`lot-${address?.lotAddress}`} name="lotAddress" defaultValue={address?.lotAddress ?? ""} /></label>
        <label><span>우편번호</span><input key={`postal-${address?.postalCode}`} name="postalCode" inputMode="numeric" defaultValue={address?.postalCode ?? ""} required /></label>
        <label className="is-wide"><span>상세주소</span><input name="addressDetail" defaultValue={project.addressDetail ?? ""} /></label>
        {addressMode === "search" && address
          ? <KakaoMapPreview selection={address} mountKakaoMap={mountKakaoMap} />
          : <div className="guest-hs-map-mock is-unverified" role="status"><strong>좌표 미확인</strong><small>수동 입력 주소는 운영자 검토가 필요합니다.</small></div>}
      </section>
      <section className="ps-card">
        <header><span>03</span><div><h2>현장 이용 정보</h2><p>현장 방문 전 운영자가 확인해야 할 정보입니다.</p></div></header>
        <label><span>출입 비밀번호</span><input name="accessPassword" autoComplete="off" /></label>
        <label><span>공실·입주 정보</span><select name="occupancyStatus" defaultValue="unknown"><option value="unknown">확인 필요</option><option value="vacant">공실</option><option value="occupied">입주 중</option></select></label>
        <label className="is-wide"><span>입주 메모</span><textarea name="moveInNote" /></label>
        <label className="is-wide"><span>특이사항</span><textarea name="specialNotes" placeholder="현장 방문 전 알아야 할 내용을 적어 주세요." /></label>
      </section>
      <section className="guest-hs-readonly-dates ps-card"><header><span aria-hidden="true">✓</span><div><h2>운영자 확인 정보</h2><p>계약 일정과 내부 관리 정보는 고객 화면에서 수정할 수 없습니다.</p></div></header></section>
      <footer><button type="button" onClick={onBack}>취소</button><button className="is-primary" type="submit" disabled={busy}>{busy ? "제출 중…" : "검토 요청 제출"}</button></footer>
      {message ? <p className="guest-hs-message" role="status" aria-live="polite">{message}</p> : null}
    </form>
  </main>;
}

export function GuestHomeStylePortal({ portalHostname = "guest.projectselmo.kr", inviteToken, initialProjectId, initialView = "project", requestGoogleCredential, selectKakaoAddress, mountKakaoMap, navigate, cleanInviteUrl }: GuestHomeStylePortalProps) {
  const [screen, setScreen] = useState<GuestScreen>(inviteToken ? "onboarding" : "login");
  const [session, setSession] = useState<GuestHomeStyleSession>();
  const [invite, setInvite] = useState<GuestInviteConsumption>();
  const [projects, setProjects] = useState<GuestHomeStyleProjectDto[]>([]);
  const [selectedId, setSelectedId] = useState(initialProjectId);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();

  const loadProjects = useCallback(async () => {
    const result = await api.guestProjects();
    setProjects(result.items);
    const nextId = initialProjectId && result.items.some((project) => project.publicProjectId === initialProjectId)
      ? initialProjectId
      : result.items[0]?.publicProjectId;
    setSelectedId(nextId);
    setScreen(initialView === "projects" ? "projects" : nextId && initialProjectId
      ? (initialView === "intake" ? "intake" : "viewer")
      : nextId && result.items.length === 1 ? "viewer" : "projects");
  }, [initialProjectId, initialView]);

  useEffect(() => {
    if (!inviteToken) return;
    const controller = new AbortController();
    setBusy(true);
    void api.consumeGuestInvite(inviteToken, controller.signal).then((next) => {
      setInvite(next);
      if (cleanInviteUrl) cleanInviteUrl();
      else globalThis.history?.replaceState({}, "", "/onboarding");
    }).catch((error) => setMessage(errorMessage(error))).finally(() => setBusy(false));
    return () => controller.abort();
  }, [cleanInviteUrl, inviteToken]);

  useEffect(() => {
    if (inviteToken) return;
    const controller = new AbortController();
    void api.guestSession(controller.signal).then((next) => {
      setSession(next);
      if (next.authenticated) void loadProjects();
    }).catch((error) => {
      if (!(error instanceof ApiError && [401, 404, 503].includes(error.status))) setMessage(errorMessage(error));
    });
    return () => controller.abort();
  }, [inviteToken, loadProjects]);

  const passwordLogin = async (email: string, password: string) => {
    setBusy(true);
    setMessage(undefined);
    try {
      setSession(await api.guestPasswordLogin(email, password));
      await loadProjects();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = async () => {
    setBusy(true);
    setMessage(undefined);
    try {
      const credential = await requestGoogleCredential?.();
      if (!credential) throw new Error("Google 인증을 완료하지 못했습니다.");
      setSession(await api.guestGoogleLogin(credential));
      await loadProjects();
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.guestLogout().catch(() => undefined);
    setSession(undefined);
    setProjects([]);
    setScreen("login");
    navigate?.("/");
  };

  const selected = useMemo(() => projects.find((project) => project.publicProjectId === selectedId), [projects, selectedId]);
  return <div className="project-selmo-design guest-home-style hs-guest-primitives" data-theme="light">
    <GuestHeader portalHostname={portalHostname} session={session} onLogout={() => void logout()} />
    {screen === "login" ? <LoginScreen onPassword={(identifier, password) => void passwordLogin(identifier, password)} onGoogle={() => void googleLogin()} googleAvailable={Boolean(requestGoogleCredential)} message={message} busy={busy} /> : null}
    {screen === "onboarding" && invite ? <OnboardingScreen invite={invite} requestGoogleCredential={requestGoogleCredential} onDone={(next) => { setSession(next); void loadProjects(); }} /> : null}
    {screen === "onboarding" && !invite ? <main className="guest-hs-loading" role="status">{busy ? "안전한 초대 링크를 확인하고 있습니다." : message ?? "초대 링크를 확인할 수 없습니다."}</main> : null}
    {screen === "projects" ? <ProjectList projects={projects} onOpen={(id) => { setSelectedId(id); setScreen("viewer"); navigate?.(`/projects/${encodeURIComponent(id)}`); }} onIntake={(id) => { setSelectedId(id); setScreen("intake"); navigate?.(`/projects/${encodeURIComponent(id)}/intake`); }} /> : null}
    {screen === "viewer" && selected ? <ProjectViewer project={selected} mountKakaoMap={mountKakaoMap} onBack={() => { setScreen("projects"); navigate?.("/projects"); }} onIntake={() => { setScreen("intake"); navigate?.(`/projects/${encodeURIComponent(selected.publicProjectId)}/intake`); }} /> : null}
    {screen === "intake" && selected ? <IntakeScreen project={selected} onBack={() => { setScreen("viewer"); navigate?.(`/projects/${encodeURIComponent(selected.publicProjectId)}`); }} selectKakaoAddress={selectKakaoAddress} mountKakaoMap={mountKakaoMap} /> : null}
  </div>;
}
